// =============================================================================
// MeuDriver — Instructor KPI Dashboard
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BookOpen,
  DollarSign,
  Star,
  TrendingUp,
  AlertTriangle,
  Info,
  MapPin,
  Clock,
  ChevronRight,
  BarChart2,
  Users,
  CheckCircle2,
  CalendarDays,
  QrCode,
  Download,
  Copy,
  Target,
  Pencil,
  Gift,
  Share2,
  Bell,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useAuth } from '@/hooks/useAuth'
import { useInstructorBookings } from '@/hooks/useBookings'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { RatingStars } from '@/components/instructor/RatingStars'
import { InstructorAdvancedStats } from '@/components/instructor/InstructorAdvancedStats'
import { InstructorWaitlistCard } from '@/components/instructor/InstructorWaitlistCard'
import { OnboardingChecklist } from '@/components/instructor/OnboardingChecklist'
import { useOnboardingStatus } from '@/hooks/useInstructorOnboarding'
import { formatCurrency, formatDateTime, formatDate, getInitials } from '@/lib/utils'
import type { InstructorRow, ReviewRow, ProfileRow } from '@/types/database'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReviewWithStudent extends ReviewRow {
  student: Pick<ProfileRow, 'full_name' | 'avatar_url'>
}

// ---------------------------------------------------------------------------
// Skeleton helpers
// ---------------------------------------------------------------------------

function KpiSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-32" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-24 mb-1" />
        <Skeleton className="h-3 w-20" />
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Profile completion
// ---------------------------------------------------------------------------

interface CompletionCheck { label: string; done: boolean; hint: string }

function calcCompletion(
  instructor: InstructorRow | null,
  profile: { avatar_url?: string | null; full_name?: string | null } | null,
): { pct: number; checks: CompletionCheck[] } {
  const checks: CompletionCheck[] = [
    { label: 'Foto de perfil',     done: !!profile?.avatar_url,                hint: 'Adicione uma foto profissional' },
    { label: 'Biografia',          done: !!instructor?.bio,                     hint: 'Conte um pouco sobre você' },
    { label: 'Preço por hora',     done: !!instructor?.hourly_rate,             hint: 'Defina sua tarifa por hora' },
    { label: 'Modelo do veículo',  done: !!instructor?.vehicle_model,           hint: 'Informe seu veículo de trabalho' },
    { label: 'Experiência (anos)', done: instructor?.years_experience != null,  hint: 'Informe seus anos de experiência' },
    { label: 'WhatsApp',           done: !!instructor?.whatsapp,                hint: 'Facilite o contato com alunos' },
    { label: 'Número da CNH',      done: !!instructor?.cnh_number,              hint: 'Aumente a confiança dos alunos' },
  ]
  const done = checks.filter((c) => c.done).length
  return { pct: Math.round((done / checks.length) * 100), checks }
}

function ProfileCompletionCard({
  instructor,
  profile,
}: {
  instructor: InstructorRow | null
  profile: { avatar_url?: string | null; full_name?: string | null } | null
}) {
  const { pct, checks } = calcCompletion(instructor, profile)
  const [expanded, setExpanded] = useState(pct < 100)
  if (pct === 100) return null

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-amber-600 shrink-0" />
          <div>
            <p className="font-semibold text-amber-900 text-sm">Complete seu perfil — {pct}%</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Perfis completos recebem até 3× mais agendamentos
            </p>
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-amber-700 hover:text-amber-900 shrink-0"
        >
          {expanded ? 'Ocultar' : 'Ver itens'}
        </button>
      </div>

      <div className="h-2 bg-amber-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-500 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      {expanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {checks.map((c) => (
            <div key={c.label} className="flex items-center gap-2 text-xs">
              {c.done ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
              ) : (
                <div className="h-3.5 w-3.5 rounded-full border-2 border-amber-400 shrink-0" />
              )}
              <span className={c.done ? 'text-gray-500 line-through' : 'text-amber-900 font-medium'}>
                {c.label}
              </span>
              {!c.done && (
                <span className="text-amber-600 truncate">{c.hint}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <Link
        to="/instrutor/editar-perfil"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 transition-colors"
      >
        Completar perfil
        <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  )
}

// ---------------------------------------------------------------------------
// QR Code section
// ---------------------------------------------------------------------------

function QrCodeSection({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false)
  const svgRef = useRef<SVGSVGElement>(null)
  const profileUrl = `${window.location.origin}/instrutores/${slug}`

  function copyUrl() {
    navigator.clipboard.writeText(profileUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function downloadQr() {
    const svg = svgRef.current
    if (!svg) return
    const svgData = new XMLSerializer().serializeToString(svg)
    const blob = new Blob([svgData], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `qrcode-${slug}.svg`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 pb-3">
        <QrCode className="h-5 w-5 text-brand-600" />
        <CardTitle className="text-base">QR Code do seu perfil</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <div className="p-3 rounded-xl border border-gray-200 bg-white shrink-0">
            <QRCodeSVG
              ref={svgRef}
              value={profileUrl}
              size={128}
              fgColor="#0054b3"
              bgColor="#ffffff"
              level="M"
            />
          </div>
          <div className="flex-1 space-y-3 text-center sm:text-left">
            <p className="text-sm text-muted-foreground">
              Compartilhe este QR code para que alunos acessem diretamente seu perfil.
            </p>
            <p className="text-xs font-mono text-gray-500 break-all bg-gray-50 rounded-lg px-3 py-2 border">
              {profileUrl}
            </p>
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              <Button variant="outline" size="sm" onClick={copyUrl} className="gap-1.5">
                <Copy className="h-3.5 w-3.5" />
                {copied ? 'Copiado!' : 'Copiar link'}
              </Button>
              <Button variant="outline" size="sm" onClick={downloadQr} className="gap-1.5">
                <Download className="h-3.5 w-3.5" />
                Baixar QR (SVG)
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Referral card
// ---------------------------------------------------------------------------

function ReferralCard({ instructorId, slug }: { instructorId: string; slug: string }) {
  const [copied, setCopied] = useState(false)
  const profileUrl = `${window.location.origin}/instrutores/${slug}`
  const refUrl = `${profileUrl}?ref=${instructorId.slice(0, 8).toUpperCase()}`

  function copyLink() {
    navigator.clipboard.writeText(refUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function shareLink() {
    if (navigator.share) {
      navigator.share({
        title: 'MeuDriver — Aulas de direção',
        text: 'Agende aulas de direção comigo pelo MeuDriver!',
        url: refUrl,
      }).catch(() => null)
    } else {
      copyLink()
    }
  }

  return (
    <Card className="overflow-hidden border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
      <CardHeader className="pb-3 flex flex-row items-center gap-2">
        <Gift className="h-5 w-5 text-orange-500 shrink-0" />
        <CardTitle className="text-base text-gray-900">Programa de Indicação</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600 leading-relaxed">
          Compartilhe seu link de indicação e conquiste novos alunos. Cada novo aluno que se cadastrar pelo seu link fortalece seu ranking na plataforma.
        </p>
        <div className="flex gap-2">
          <div className="flex-1 min-w-0 bg-white rounded-xl border border-orange-200 px-3 py-2 text-xs font-mono text-orange-700 truncate">
            {refUrl}
          </div>
          <button
            onClick={copyLink}
            className="shrink-0 px-3 h-9 rounded-xl border border-orange-200 bg-white text-gray-600 hover:border-orange-400 hover:text-orange-600 transition-colors flex items-center gap-1.5 text-xs font-medium"
          >
            <Copy className="h-3.5 w-3.5" />
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
          <button
            onClick={shareLink}
            className="shrink-0 px-3 h-9 rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-colors flex items-center gap-1.5 text-xs font-semibold"
          >
            <Share2 className="h-3.5 w-3.5" />
            Compartilhar
          </button>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function InstructorDashboard() {
  const { user, profile } = useAuth()

  const [instructor, setInstructor] = useState<InstructorRow | null>(null)
  const [instructorLoading, setInstructorLoading] = useState(true)

  const [reviews, setReviews] = useState<ReviewWithStudent[]>([])
  const [reviewsLoading, setReviewsLoading] = useState(true)

  const { data: allBookings = [], isLoading: bookingsLoading } = useInstructorBookings()
  const { data: onboardingStatus } = useOnboardingStatus()

  // ── Fetch instructor row ──────────────────────────────────────────────────

  useEffect(() => {
    if (!user?.id) return

    async function loadInstructor() {
      setInstructorLoading(true)
      const { data } = await supabase
        .from('instructors')
        .select('*')
        .eq('profile_id', user!.id)
        .single()

      setInstructor(data ?? null)
      setInstructorLoading(false)
    }

    loadInstructor()
  }, [user?.id])

  // ── Fetch recent reviews ──────────────────────────────────────────────────

  useEffect(() => {
    if (!instructor?.id) return

    async function loadReviews() {
      setReviewsLoading(true)
      const { data } = await supabase
        .from('reviews')
        .select(`*, student:profiles!reviews_student_id_fkey(full_name, avatar_url)`)
        .eq('instructor_id', instructor!.id)
        .order('created_at', { ascending: false })
        .limit(3)

      setReviews((data ?? []) as ReviewWithStudent[])
      setReviewsLoading(false)
    }

    loadReviews()
  }, [instructor?.id])

  // ── Derived KPI values ────────────────────────────────────────────────────

  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString()

  const monthBookings = allBookings.filter((b) => {
    const at = b.scheduled_at
    return (
      at >= monthStart &&
      at <= monthEnd &&
      (b.status === 'completed' || b.status === 'in_progress')
    )
  })

  const monthRevenue = monthBookings.reduce(
    (sum, b) => sum + (b.instructor_payout ?? 0),
    0,
  )

  const upcomingBookings = allBookings
    .filter((b) => b.status === 'confirmed' && b.scheduled_at >= now.toISOString())
    .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))
    .slice(0, 5)

  const nextSoonLesson = (() => {
    const first = upcomingBookings[0]
    if (!first) return null
    const diffMs = new Date(first.scheduled_at).getTime() - now.getTime()
    return diffMs > 0 && diffMs <= 24 * 60 * 60 * 1000 ? first : null
  })()
  const hoursUntilNext = nextSoonLesson
    ? Math.max(1, Math.floor((new Date(nextSoonLesson.scheduled_at).getTime() - now.getTime()) / (1000 * 60 * 60)))
    : 0

  // ── Analytics computations ───────────────────────────────────────────────

  const analytics = useMemo(() => {
    const finished = allBookings.filter((b) =>
      b.status === 'completed' || b.status === 'cancelled_by_student' ||
      b.status === 'cancelled_by_instructor' || b.status === 'no_show'
    )
    const completed = allBookings.filter((b) => b.status === 'completed')
    const completionRate = finished.length
      ? Math.round((completed.length / finished.length) * 100)
      : 0

    // Peak hours (0–23)
    const hourCounts = Array<number>(24).fill(0)
    allBookings.forEach((b) => {
      const h = new Date(b.scheduled_at).getHours()
      hourCounts[h]++
    })
    const maxHour = Math.max(...hourCounts, 1)
    // Only show 6h–22h range
    const hourSlots = Array.from({ length: 17 }, (_, i) => i + 6).map((h) => ({
      label: `${String(h).padStart(2, '0')}h`,
      count: hourCounts[h],
      pct: Math.round((hourCounts[h] / maxHour) * 100),
    }))

    // Day of week distribution
    const DOW_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
    const dowCounts = Array<number>(7).fill(0)
    allBookings.forEach((b) => {
      const d = new Date(b.scheduled_at).getDay()
      dowCounts[d]++
    })
    const maxDow = Math.max(...dowCounts, 1)
    const dowSlots = DOW_LABELS.map((label, i) => ({
      label,
      count: dowCounts[i],
      pct: Math.round((dowCounts[i] / maxDow) * 100),
    }))

    // Recurring students
    const studentMap: Record<string, number> = {}
    allBookings.forEach((b) => {
      studentMap[b.student_id] = (studentMap[b.student_id] ?? 0) + 1
    })
    const recurringCount = Object.values(studentMap).filter((c) => c > 1).length
    const totalStudents = Object.keys(studentMap).length

    return { completionRate, hourSlots, dowSlots, recurringCount, totalStudents }
  }, [allBookings])

  // ── Status banner ─────────────────────────────────────────────────────────

  // ── Monthly goal (localStorage) ──────────────────────────────────────────

  const goalKey = instructor?.id ? `md-revenue-goal-${instructor.id}` : null
  const [monthlyGoal, setMonthlyGoal] = useState(0)
  const [editingGoal, setEditingGoal] = useState(false)
  const [goalInput, setGoalInput] = useState('')

  useEffect(() => {
    if (!goalKey) return
    const stored = localStorage.getItem(goalKey)
    if (stored) setMonthlyGoal(Number(stored))
    else setEditingGoal(true)
  }, [goalKey])

  function handleSaveGoal() {
    const v = Number(goalInput)
    if (!v || v <= 0 || !goalKey) return
    localStorage.setItem(goalKey, String(v))
    setMonthlyGoal(v)
    setEditingGoal(false)
    setGoalInput('')
  }

  const firstName = profile?.full_name?.split(' ')[0] ?? 'Instrutor'

  const isLoading = instructorLoading || bookingsLoading

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Olá, {firstName}! Aqui está seu resumo
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {formatDate(new Date(), "EEEE, d 'de' MMMM 'de' yyyy")}
        </p>
      </div>

      {/* Status banners */}
      {!instructorLoading && instructor?.status === 'pending' && (
        <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <Info className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-yellow-800">Cadastro em análise</p>
            <p className="text-sm text-yellow-700 mt-0.5">
              Seu cadastro está em análise. Em breve você receberá a aprovação.
              Enquanto isso, você pode completar seu perfil.
            </p>
          </div>
        </div>
      )}

      {!instructorLoading && instructor?.status === 'suspended' && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-red-800">Conta suspensa</p>
            <p className="text-sm text-red-700 mt-0.5">
              Sua conta foi suspensa. Entre em contato com o suporte para mais
              informações e para regularizar sua situação.
            </p>
          </div>
        </div>
      )}

      {/* 24h lesson reminder */}
      {!bookingsLoading && nextSoonLesson && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <Bell className="h-5 w-5 text-amber-600 shrink-0 animate-pulse" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-900 text-sm">
              Aula em {hoursUntilNext}h — prepare-se!
            </p>
            <p className="text-xs text-amber-700 mt-0.5 truncate">
              Aluno:{' '}
              <strong>{(nextSoonLesson as unknown as { student?: { full_name?: string } }).student?.full_name ?? 'Aluno'}</strong>
              {' '}·{' '}{formatDateTime(nextSoonLesson.scheduled_at)}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-300 text-amber-800 hover:bg-amber-100 shrink-0 text-xs h-7"
            asChild
          >
            <Link to="/instrutor/aulas">Ver aulas</Link>
          </Button>
        </div>
      )}

      {/* Onboarding checklist — exibido enquanto não completado */}
      {!instructorLoading && onboardingStatus && !onboardingStatus.is_complete && (
        <OnboardingChecklist status={onboardingStatus} />
      )}

      {/* Profile completion */}
      {!instructorLoading && instructor && (
        <ProfileCompletionCard instructor={instructor} profile={profile} />
      )}

      {/* KPI cards — 2-col mobile, 4-col desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <KpiSkeleton />
            <KpiSkeleton />
            <KpiSkeleton />
            <KpiSkeleton />
          </>
        ) : (
          <>
            {/* 1. Aulas este mês */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Aulas este mês
                </CardTitle>
                <BookOpen className="h-4 w-4 text-teal-600" />
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">
                  {monthBookings.length}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  aulas realizadas
                </p>
              </CardContent>
            </Card>

            {/* 2. Faturamento bruto */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Faturamento bruto
                </CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-gray-900 leading-tight">
                  {formatCurrency(monthRevenue)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  repasse este mês
                </p>
              </CardContent>
            </Card>

            {/* 3. Avaliação média */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Avaliação média
                </CardTitle>
                <Star className="h-4 w-4 text-orange-400" />
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">
                  {(instructor?.rating_avg ?? 0).toFixed(1)}
                </p>
                <RatingStars
                  rating={instructor?.rating_avg ?? 0}
                  size="sm"
                  className="mt-1"
                />
              </CardContent>
            </Card>

            {/* 4. Total de aulas */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total de aulas
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-brand-600" />
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">
                  {instructor?.total_lessons ?? 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  ao longo do tempo
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Monthly goal */}
      {!isLoading && instructor && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-brand-600" />
                <p className="text-sm font-semibold text-gray-900">Meta de faturamento mensal</p>
              </div>
              {monthlyGoal > 0 && !editingGoal && (
                <button
                  onClick={() => { setGoalInput(String(monthlyGoal)); setEditingGoal(true) }}
                  className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 transition-colors"
                >
                  <Pencil className="h-3 w-3" />
                  Editar
                </button>
              )}
            </div>

            {editingGoal ? (
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-2.5 text-xs text-muted-foreground">R$</span>
                  <input
                    type="number"
                    min={100}
                    step={100}
                    placeholder="Ex: 3000"
                    value={goalInput}
                    onChange={(e) => setGoalInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveGoal()}
                    className="w-full pl-8 h-9 rounded-md border border-input bg-background px-3 text-sm"
                  />
                </div>
                <Button size="sm" onClick={handleSaveGoal} disabled={!goalInput || Number(goalInput) <= 0}>
                  Salvar
                </Button>
                {monthlyGoal > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setEditingGoal(false)}>
                    Cancelar
                  </Button>
                )}
              </div>
            ) : monthlyGoal > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatCurrency(monthRevenue)} recebidos</span>
                  <span>Meta: {formatCurrency(monthlyGoal)}</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${monthRevenue >= monthlyGoal ? 'bg-emerald-500' : 'bg-brand-500'}`}
                    style={{ width: `${Math.min(100, Math.round((monthRevenue / monthlyGoal) * 100))}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {Math.min(100, Math.round((monthRevenue / monthlyGoal) * 100))}% da meta atingida
                  {monthRevenue >= monthlyGoal && ' 🎉'}
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Próximas aulas */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Próximas aulas</h2>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/instrutor/aulas" className="flex items-center gap-1 text-teal-600">
              Ver todas
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        {bookingsLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-56" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : upcomingBookings.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <BookOpen className="h-10 w-10 text-gray-300 mb-3" />
              <p className="font-medium text-gray-500">Nenhuma aula confirmada</p>
              <p className="text-sm text-muted-foreground mt-1">
                Suas próximas aulas aparecerão aqui.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {upcomingBookings.map((booking) => {
              const bookingWithStudent = booking as typeof booking & {
                student?: { full_name?: string | null; avatar_url?: string | null }
              }
              const studentName = bookingWithStudent.student?.full_name ?? 'Aluno'
              return (
                <Card key={booking.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700 font-semibold text-sm">
                        {getInitials(studentName)}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-gray-900 truncate">
                            {studentName}
                          </p>
                          <Badge variant="outline" className="bg-brand-50 text-brand-700 border-brand-200 shrink-0">
                            Confirmada
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDateTime(booking.scheduled_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5 opacity-0" />
                            {booking.duration_minutes} min
                          </span>
                          {booking.pickup_address && (
                            <span className="flex items-center gap-1 truncate max-w-xs">
                              <MapPin className="h-3.5 w-3.5 shrink-0" />
                              {booking.pickup_address}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Link */}
                      <Button variant="ghost" size="sm" asChild className="shrink-0">
                        <Link to="/instrutor/aulas" className="text-teal-600">
                          Ver detalhes
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {/* Analytics / Relatórios */}
      {!isLoading && allBookings.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-teal-600" />
            Relatórios
          </h2>

          {/* Summary row */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Completion rate */}
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-green-500 shrink-0" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{analytics.completionRate}%</p>
                  <p className="text-xs text-muted-foreground">Taxa de conclusão</p>
                </div>
              </CardContent>
            </Card>

            {/* Recurring students */}
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Users className="h-8 w-8 text-brand-500 shrink-0" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{analytics.recurringCount}</p>
                  <p className="text-xs text-muted-foreground">
                    alunos recorrentes de {analytics.totalStudents}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Best day */}
            <Card className="col-span-2 lg:col-span-1">
              <CardContent className="p-4 flex items-center gap-3">
                <CalendarDays className="h-8 w-8 text-orange-400 shrink-0" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.dowSlots.reduce((best, d) => d.count > best.count ? d : best, analytics.dowSlots[0]).label}
                  </p>
                  <p className="text-xs text-muted-foreground">dia mais agendado</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Peak hours chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <Clock className="h-4 w-4" /> Horários de pico
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="flex items-end gap-1 h-20">
                {analytics.hourSlots.map((slot) => (
                  <div key={slot.label} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full rounded-sm bg-teal-400 transition-all"
                      style={{ height: `${Math.max(slot.pct, slot.count > 0 ? 8 : 2)}%` }}
                      title={`${slot.count} aula(s)`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-1 mt-1">
                {analytics.hourSlots.map((slot) => (
                  <div key={slot.label} className="flex-1 text-center text-[9px] text-muted-foreground leading-none">
                    {slot.label}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Day of week chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4" /> Distribuição por dia da semana
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="space-y-2">
                {analytics.dowSlots.map((slot) => (
                  <div key={slot.label} className="flex items-center gap-2">
                    <span className="w-7 text-xs text-right text-muted-foreground">{slot.label}</span>
                    <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-500 rounded-full transition-all"
                        style={{ width: `${slot.pct}%` }}
                      />
                    </div>
                    <span className="w-6 text-xs text-muted-foreground">{slot.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats avançadas — "Seus números" */}
      {!instructorLoading && instructor?.id && (
        <InstructorAdvancedStats
          instructorId={instructor.id}
          hourlyRate={instructor.hourly_rate ?? null}
          city={profile?.city ?? null}
          bookings={allBookings}
        />
      )}

      {/* Lista de espera */}
      {!instructorLoading && instructor?.id && (
        <InstructorWaitlistCard instructorId={instructor.id} />
      )}

      {/* QR Code do perfil */}
      {!instructorLoading && instructor?.slug && (
        <QrCodeSection slug={instructor.slug} />
      )}

      {/* Programa de indicação */}
      {!instructorLoading && instructor?.slug && instructor?.id && (
        <ReferralCard instructorId={instructor.id} slug={instructor.slug} />
      )}

      {/* Avaliações recentes */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Avaliações recentes</h2>

        {reviewsLoading ? (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Star className="h-10 w-10 text-gray-300 mb-3" />
              <p className="font-medium text-gray-500">Nenhuma avaliação ainda</p>
              <p className="text-sm text-muted-foreground mt-1">
                Suas avaliações de alunos aparecerão aqui.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reviews.map((review) => (
              <Card key={review.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 font-semibold text-sm">
                      {getInitials(review.student?.full_name ?? 'A')}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-gray-900">
                            {review.student?.full_name ?? 'Aluno'}
                          </p>
                          <RatingStars rating={review.rating} size="sm" />
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                      {review.comment && (
                        <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">
                          "{review.comment}"
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
